import type { CSSProperties, ReactNode } from 'react';
import { Icon, type IconName } from './Icon';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'outline'
  | 'ghost'
  | 'ghost-primary'
  | 'danger'
  | 'danger-outline';

export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children?: ReactNode;
  icon?: IconName;
  iconRight?: IconName;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  style?: CSSProperties;
  title?: string;
}

const VARIANT_STYLES: Record<ButtonVariant, CSSProperties> = {
  primary: { background: 'var(--bx-primary)', color: '#fff' },
  secondary: { background: 'var(--bx-surface-2)', color: 'var(--bx-text-dark)' },
  outline: { background: '#fff', color: 'var(--bx-text-dark)', borderColor: 'var(--bx-border)' },
  ghost: { background: 'transparent', color: 'var(--bx-text-dark)' },
  'ghost-primary': { background: 'transparent', color: 'var(--bx-primary)' },
  danger: { background: 'var(--bx-error)', color: '#fff' },
  'danger-outline': {
    background: '#fff',
    color: 'var(--bx-error)',
    borderColor: 'var(--bx-error)',
  },
};

export function Button({
  variant = 'primary',
  size = 'md',
  children,
  icon,
  iconRight,
  onClick,
  disabled,
  type = 'button',
  style,
  title,
}: ButtonProps) {
  const base: CSSProperties = {
    fontFamily: 'var(--bx-font-sans)',
    fontWeight: 500,
    borderRadius: 10,
    border: '1px solid transparent',
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    height: size === 'sm' ? 30 : size === 'lg' ? 44 : 36,
    padding: size === 'sm' ? '0 12px' : size === 'lg' ? '0 22px' : '0 14px',
    fontSize: size === 'sm' ? 12.5 : 13.28,
    opacity: disabled ? 0.55 : 1,
    transition: 'background 120ms, border-color 120ms',
    whiteSpace: 'nowrap',
  };
  return (
    <button
      type={type}
      title={title}
      style={{ ...base, ...VARIANT_STYLES[variant], ...style }}
      onClick={disabled ? undefined : onClick}
    >
      {icon && <Icon name={icon} size={size === 'sm' ? 13 : 14} />}
      {children}
      {iconRight && <Icon name={iconRight} size={size === 'sm' ? 13 : 14} />}
    </button>
  );
}
